// ── Currency symbol map ────────────────────────────────────────────────────
export const CURRENCY_SYMBOLS = {
  USD: "$",    // 🇺🇸 US
  TRY: "₺",   // 🇹🇷 Turkey
  EUR: "€",    // 🇪🇺 Eurozone
  GBP: "£",    // 🇬🇧 UK
  INR: "₹",   // 🇮🇳 India
  JPY: "¥",    // 🇯🇵 Japan
  HKD: "HK$",  // 🇭🇰 Hong Kong
  CAD: "CA$",  // 🇨🇦 Canada
  CNY: "¥",    // 🇨🇳 China
};

// Market code → currency code map (mirrors API response)
const MARKET_CURRENCY = {
  US:  "USD",
  TR:  "TRY",
  IN:  "INR",
  JP:  "JPY",
  GB:  "GBP",
  HK:  "HKD",
  CA:  "CAD",
  CN:  "CNY",
  ALL: null,
};

/**
 * Returns the currency symbol for a given market code.
 * Falls back to the raw currency code if no symbol is mapped.
 *
 * @param {string} marketCode - e.g. "US", "TR"
 * @returns {string} - e.g. "$", "₺"
 */
export const getSymbolForMarket = (marketCode) => {
  const currency = MARKET_CURRENCY[marketCode] ?? null;
  if (!currency) return "";
  return CURRENCY_SYMBOLS[currency] ?? currency;
};

/**
 * Reads the active market from sessionStorage and returns its currency symbol.
 * Drop-in replacement for hardcoded "$".
 *
 * @returns {string} - e.g. "$", "₺"
 */
export const getCurrencySymbol = () => {
  const market = sessionStorage.getItem("selectedMarket") ?? "US";
  return getSymbolForMarket(market);
};

/**
 * Locale-aware number formatter. Drop-in replacement for .toFixed(d).
 * Respects the browser's locale (e.g. German → comma as decimal separator).
 *
 * @param {number|string} v   - value to format
 * @param {number}        d   - decimal places (default 2)
 * @returns {string}          - e.g. "1,234.56" (en) or "1.234,56" (de)
 */
export const fmtL = (v, d = 2) => {
  const n = Number(v);
  if (v === null || v === undefined || isNaN(n)) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
};

/**
 * Formats a number with the correct currency symbol for the active market.
 *
 * @param {number|string} value
 * @param {object} options
 * @param {boolean} options.compact   - e.g. 1.2M instead of 1,200,000
 * @param {number}  options.decimals  - decimal places (default 2)
 * @returns {string} - e.g. "$1,234.56", "₺1.2M"
 */
export const formatCurrency = (value, { compact = false, decimals = 2 } = {}) => {
  const symbol = getCurrencySymbol();
  const num = parseFloat(value);
  if (isNaN(num)) return `${symbol}—`;

  if (compact) {
    const abs = Math.abs(num);
    const sign = num < 0 ? "-" : "";
    if (abs >= 1e12) return `${sign}${symbol}${fmtL(abs / 1e12, decimals)}T`;
    if (abs >= 1e9)  return `${sign}${symbol}${fmtL(abs / 1e9, decimals)}B`;
    if (abs >= 1e6)  return `${sign}${symbol}${fmtL(abs / 1e6, decimals)}M`;
    if (abs >= 1e3)  return `${sign}${symbol}${fmtL(abs / 1e3, decimals)}K`;
    return `${sign}${symbol}${fmtL(abs, decimals)}`;
  }

  return `${symbol}${fmtL(num, decimals)}`;
};